import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import GallerySection from './components/GallerySection';
import PricingSidebar from './components/PricingSidebar';
import DayByDay from './components/DayByDay';
import { useTourDetail } from '@/hooks/useTourDetail';

const NAV_ITEMS = [
  { id: 's-glance', label: 'td.nav.glance' },
  { id: 's-route', label: 'td.nav.route' },
  { id: 's-about', label: 'td.nav.about' },
  { id: 's-iti', label: 'td.nav.iti' },
  { id: 's-inex', label: 'td.nav.inex' },
  { id: 's-rv', label: 'td.nav.rv' },
  { id: 's-fit', label: 'td.nav.fit' },
  { id: 's-faq', label: 'td.nav.faq' },
];

export default function TourDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { tour, images, itinerary, reviews, faqs, loading, error } = useTourDetail(id || '');
  const [faqOpen, setFaqOpen] = useState<number | null>(0);
  const [activeNav, setActiveNav] = useState('s-faq');
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const [reviewScroll, setReviewScroll] = useState(0);
  const reviewContainerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = NAV_ITEMS.map((item) => {
        const el = document.getElementById(item.id);
        if (!el) return { id: item.id, top: Infinity };
        const rect = el.getBoundingClientRect();
        return { id: item.id, top: rect.top - 140 };
      });
      const visible = offsets.filter((o) => o.top <= 20).sort((a, b) => b.top - a.top);
      if (visible.length > 0) {
        setActiveNav(visible[0].id);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (targetId: string) => {
    const el = document.getElementById(targetId);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollReviews = (dir: 'left' | 'right') => {
    const container = reviewContainerRef.current;
    if (!container) return;
    const cardWidth = container.scrollWidth / reviews.length;
    const scrollAmount = Math.max(cardWidth * 1.2, 300);
    const target = dir === 'left'
      ? Math.max(0, container.scrollLeft - scrollAmount)
      : container.scrollLeft + scrollAmount;
    container.scrollTo({ left: target, behavior: 'smooth' });
    setReviewScroll(target);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <div className="mx-auto max-w-[1240px] px-4 md:px-6 pt-4 md:pt-6">
            <div className="aspect-[2/1] bg-secondary-100 rounded-xl animate-pulse" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !tour) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <div className="mx-auto max-w-[1240px] px-4 md:px-6 py-20 text-center">
            <i className="ri-error-warning-line text-5xl text-secondary-300 mb-4" />
            <h2 className="font-heading text-xl font-bold text-secondary-800 mb-2">{t('td.notFound')}</h2>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line" />{t('td.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const galleryImages = images.map((img) => img.image_url);

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="mx-auto max-w-[1240px] px-4 md:px-6 pb-[88px] lg:pb-5">
          {/* Gallery */}
          <div className="mt-5">
            <GallerySection images={galleryImages} title={tour.title} />
          </div>

          {/* Breadcrumb */}
          <nav className="py-4" aria-label={t('td.breadcrumb')}>
            <ol className="flex flex-wrap items-center gap-2 text-[12px] text-secondary-400">
              {(tour.breadcrumb || []).map((item: string, idx: number, arr: string[]) => (
                <li key={idx} className="flex items-center gap-2">
                  {idx > 0 && <span className="text-secondary-300">&gt;</span>}
                  {idx === arr.length - 1 ? (
                    <span className="text-secondary-700 line-clamp-1">{item}</span>
                  ) : idx === 0 ? (
                    <Link to="/" className="hover:text-primary-600 transition-colors">{item}</Link>
                  ) : (
                    <Link to="/tours" className="hover:text-primary-600 transition-colors">{item}</Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Sticky Nav */}
        <nav
          ref={navRef}
          aria-label={t('td.pageSections')}
          className="sticky top-[65px] z-[90] hidden border-y border-background-200 bg-[rgba(248,250,252,0.96)] backdrop-blur-[10px] lg:block"
        >
          <div className="mx-auto max-w-[1240px] overflow-x-auto px-4 md:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <ul className="flex gap-0.5">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollTo(item.id)}
                    className={`inline-block whitespace-nowrap px-3.5 py-3 text-[13.5px] font-medium transition-colors hover:text-primary-600 border-b-2 ${
                      activeNav === item.id
                        ? 'border-b-primary-500 font-semibold text-primary-600'
                        : 'border-transparent text-secondary-500'
                    }`}
                  >
                    {t(item.label)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="mx-auto max-w-[1240px] px-4 md:px-6 pt-[26px]">
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,860px)_356px]">
            <main>
              {/* Title Card */}
              <div className="rounded-2xl border border-background-200 bg-white p-[26px_28px] shadow-[0_1px_2px_rgba(19,48,44,0.05)]">
                <div className="mb-5">
                  <h1 className="text-[22px] md:text-[26px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950 mb-2">{tour.title}</h1>
                  <div className="flex flex-wrap items-center gap-2 text-[13px] text-secondary-500">
                    <span className="inline-flex items-center gap-1 text-secondary-600">
                      <i className="ri-star-fill text-primary-500 text-xs" />
                      <span className="font-bold text-secondary-800">{tour.rating_score}</span>
                      <span>{t('td.on')} {tour.rating_source}</span>
                    </span>
                    <span className="w-1 h-1 rounded-full bg-secondary-300" />
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-shield-check-line text-xs" />
                      {t('td.licensed')}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-secondary-300" />
                    <a href="/why-us" className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-0.5">
                      {tour.different_link}
                      <i className="ri-arrow-right-s-line" />
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {(tour.tags || []).map((tag: { icon: string; label: string; plain?: boolean }, idx: number) => {
                      const isPlain = idx >= 2;
                      return (
                        <span
                          key={tag.label}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md ${
                            isPlain
                              ? 'border border-background-200 text-secondary-600 bg-white'
                              : 'bg-secondary-100 text-secondary-700'
                          }`}
                        >
                          <i className={tag.icon} />
                          {tag.label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Description box */}
                <div className="flex items-start gap-3 rounded-[13px] border border-[#CDE9E7] bg-primary-50/50 p-[17px_19px]">
                  <div className="min-w-0">
                    <p className="mb-1.5 text-[13.5px] font-semibold leading-[1.4] text-foreground-950">
                      {t('td.realJourney')}
                    </p>
                    {(tour.description || '').split('\n\n').map((para: string, i: number) => (
                      <p key={i} className="text-[13.5px] leading-[1.65] text-secondary-600">
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* At a glance */}
              <section id="s-glance" className="scroll-mt-[130px] mt-8">
                <h2 className="mb-3.5 text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">
                  {t('td.nav.glance')} <span className="text-[12px] font-medium text-secondary-400">{t('td.glanceSub')}</span>
                </h2>
                <div className="overflow-hidden rounded-xl border border-background-200 bg-white shadow-[0_1px_2px_rgba(19,48,44,0.05)]">
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    {[
                      { key: 'duration', label: t('td.glance.duration'), defaultSub: t('td.glance.departsAnyDay') },
                      { key: 'destinations', label: t('td.glance.destinations'), defaultSub: t('td.glance.halong') },
                      { key: 'tourPace', label: t('td.glance.tourPace') },
                      { key: 'physicalRating', label: t('td.glance.physicalRating') },
                      { key: 'gettingAround', label: t('td.glance.gettingAround'), defaultSub: t('td.glance.overland') },
                      { key: 'bestSeason', label: t('td.glance.bestSeason') },
                      { key: 'indicativeFrom', label: t('td.glance.indicativeFrom'), fullWidth: true },
                    ]
                      .filter((item) => tour.at_a_glance && (tour.at_a_glance as Record<string, string>)[item.key] !== undefined)
                      .map((item, idx, arr) => {
                        const value = (tour.at_a_glance as Record<string, string>)[item.key];
                        const parts = String(value).split('—');
                        const isLeftCol = idx % 2 === 0;
                        const hasBorderBottom = idx < arr.length - 1;
                        const hasBorderRight = isLeftCol && !item.fullWidth;
                        const subText = parts[1]?.trim() || item.defaultSub || '';
                        return (
                          <div
                            key={item.key}
                            className={`grid grid-cols-1 gap-1.5 px-[17px] py-[13px] items-start sm:grid-cols-[112px_1fr] sm:gap-3 ${hasBorderBottom ? 'border-b border-background-200/70' : ''} ${hasBorderRight ? 'sm:border-r sm:border-background-200/70' : ''} ${item.fullWidth ? 'sm:col-span-2' : ''}`}
                          >
                            <p className="pt-0 text-[11px] font-semibold uppercase tracking-[0.04em] text-secondary-400 sm:pt-0.5 sm:text-[12px] sm:font-medium sm:normal-case sm:tracking-normal">{item.label}</p>
                            <div>
                              <p className="text-[13.5px] font-semibold leading-[1.5] text-foreground-950">{parts[0].trim()}</p>
                              {subText && (
                                <p className="mt-0.5 text-[12px] font-normal not-italic leading-[1.5] text-secondary-400">{subText}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </section>

              {/* Route */}
              <section id="s-route" className="scroll-mt-[130px] mt-8">
                <h2 className="mb-3.5 text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.route')}</h2>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 rounded-xl bg-primary-50/60 p-4">
                  {(tour.route?.points || []).map((point: string, idx: number, arr: string[]) => (
                    <span key={point} className="inline-flex items-center gap-1.5">
                      <span className="text-[13.5px] font-semibold text-foreground-950">{point}</span>
                      {idx < arr.length - 1 && <span className="px-0.5 text-[14px] text-secondary-400">→</span>}
                    </span>
                  ))}
                </div>
                <p className="mt-2.5 text-[12px] text-secondary-400 leading-relaxed">{tour.route?.description}</p>
              </section>

              {/* About this trip */}
              <section id="s-about" className="scroll-mt-[130px] mt-8">
                <h2 className="mb-3.5 text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.about')}</h2>
                <div className="[&_p]:mb-3 [&_p:last-child]:mb-0 [&_p]:text-[15px] [&_p]:leading-[1.7] [&_p]:text-secondary-600">
                  {(tour.about_this_trip || '').split('\n\n').map((para: string, i: number) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </section>

              {/* Day by day */}
              <div id="s-iti" className="scroll-mt-[130px] mt-8">
                <DayByDay days={itinerary.map((d) => ({
                  day: d.day_number,
                  title: d.title,
                  subtitle: d.subtitle || undefined,
                  content: d.content || undefined,
                  afternoonTitle: d.afternoon_title || undefined,
                  afternoonContent: d.afternoon_content || undefined,
                  images: d.images || undefined,
                  timeline: d.timeline || undefined,
                  meals: (d as { meals?: { breakfast?: boolean; lunch?: boolean; dinner?: boolean } }).meals || undefined,
                }))} />
              </div>

              {/* What this journey includes */}
              <div id="s-inex" className="scroll-mt-[130px] mt-8">
                <h2 className="mb-3.5 text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.includes')}</h2>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div className="rounded-[13px] border border-background-200 px-[19px] pb-[19px] pt-[17px]">
                    <div className="mb-3 flex items-center gap-2 border-b border-background-200/70 pb-[11px] text-[16px] font-bold">
                      <span className="grid size-[23px] place-items-center rounded-full bg-primary-500 text-white text-xs">
                        <i className="ri-check-line" />
                      </span>
                      <span className="text-primary-600">{t('td.included')}</span>
                    </div>
                    <ul className="grid list-none gap-2.5">
                      {(tour.includes || []).map((item: string) => (
                        <li key={item} className="relative pl-[22px] text-[13.5px] leading-[1.55] text-secondary-600">
                          <span className="absolute left-0 top-1 size-[5px] rounded-full bg-primary-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-[13px] border border-background-200 px-[19px] pb-[19px] pt-[17px]">
                    <div className="mb-3 flex items-center gap-2 border-b border-background-200/70 pb-[11px] text-[16px] font-bold">
                      <span className="grid size-[23px] place-items-center rounded-full bg-secondary-300 text-white text-xs">
                        <i className="ri-close-line" />
                      </span>
                      <span className="text-secondary-400">{t('td.excluded')}</span>
                    </div>
                    <ul className="grid list-none gap-2.5">
                      {(tour.excludes || []).map((item: string) => (
                        <li key={item} className="relative pl-[22px] text-[13.5px] leading-[1.55] text-secondary-600">
                          <span className="absolute left-0 top-1 size-[5px] rounded-full bg-secondary-300" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-secondary-50/60 p-4 text-[12px] text-secondary-500">
                  <i className="ri-information-line text-secondary-400 shrink-0 mt-0.5" />
                  <p>{t('td.includesNote')}</p>
                </div>
              </div>

              {/* Reviews */}
              <div id="s-rv" className="scroll-mt-[130px] mt-8">
                <section className="flex w-full flex-col gap-3.5">
                  <h2 className="text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.reviewsTitle')}</h2>
                  <div className="mb-3.5 flex flex-wrap items-center gap-3.5 rounded-xl bg-primary-50/60 px-4 py-3.5">
                    <span className="text-[26px] font-extrabold leading-none tracking-[-0.02em] text-primary-600">{tour.rating_score}</span>
                    <span className="text-[12px] leading-[1.4] text-secondary-500">
                      <b className="block text-[13.5px] font-semibold text-foreground-950">{t('td.excellent')}</b>
                      {t('td.verifiedReviews')}
                    </span>
                    <a href="https://www.tripadvisor.com.vn/Attraction_Review-g293924-d28222088-Reviews-Meetup_Travel-Hanoi.html" target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-primary-600">
                      {t('td.readAllReviews')} <i className="ri-arrow-right-line" />
                    </a>
                  </div>

                  {/* Review carousel */}
                  <div className="relative">
                    <div
                      ref={reviewContainerRef}
                      className="flex -ml-3 lg:-ml-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory pb-2"
                    >
                      {reviews.map((review) => (
                        <div key={review.id} className="min-w-0 shrink-0 grow-0 basis-full pl-3 sm:basis-[55%] md:basis-[40%] lg:basis-[36%] lg:pl-4 snap-start">
                          <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-background-200 bg-white shadow-[0_2px_12px_-6px_rgba(15,23,42,0.12)]">
                            {review.images && review.images.length > 0 && (
                              <div className="relative aspect-[249/160] w-full overflow-hidden">
                                <img src={review.images[0]} alt={`${review.author} review photo`} className="w-full h-full object-cover" loading="lazy" />
                              </div>
                            )}
                            <div className="flex flex-1 flex-col px-3 py-3 lg:px-4 lg:py-4">
                              <div className="flex items-center gap-3">
                                <div className="relative shrink-0">
                                  {review.avatar ? (
                                    <div className="relative h-12 w-12 overflow-hidden rounded-full ring-2 ring-white bg-secondary-100">
                                      <img src={review.avatar} alt={review.author} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                  ) : (
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#00aa6c] text-xs font-semibold text-white">
                                      {review.author.charAt(0).toUpperCase()}
                                    </span>
                                  )}
                                  <span className="absolute -bottom-1 -right-1 flex h-[20px] w-[20px] items-center justify-center overflow-hidden rounded-full bg-white ring-2 ring-white">
                                    <img src="https://www.google.com/s2/favicons?domain=tripadvisor.com&sz=64" alt="TripAdvisor" width={20} height={20} className="h-full w-full object-contain" loading="lazy" />
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-[11px] font-semibold text-foreground-950">{review.author}</p>
                                  <p className="text-[11px] text-secondary-400">{review.review_date}</p>
                                </div>
                              </div>
                              <div className="mt-2 flex items-center gap-0.5">
                                {Array.from({ length: review.rating }).map((_, i) => (
                                  <i key={i} className="ri-star-fill text-[#00aa6c] text-[11px]" />
                                ))}
                              </div>
                              <h3 className="mt-3 text-[12px] font-semibold text-foreground-950">{review.title}</h3>
                              <p className={`mt-1 text-[12px] leading-[1.55] text-secondary-500 ${expandedReviews.has(review.id) ? '' : 'line-clamp-4'}`}>
                                {review.content}
                              </p>
                              {review.content.length > 200 && (
                                <button
                                  onClick={() => {
                                    const next = new Set(expandedReviews);
                                    if (next.has(review.id)) next.delete(review.id);
                                    else next.add(review.id);
                                    setExpandedReviews(next);
                                  }}
                                  className="mt-1 self-start text-[12px] font-medium text-primary-600 hover:underline"
                                >
                                  {expandedReviews.has(review.id) ? t('td.showLess') : t('td.readMore')}
                                </button>
                              )}
                              <div className="mt-auto flex items-center gap-2 pt-3">
                                <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-white">
                                  <img src="https://www.google.com/s2/favicons?domain=tripadvisor.com&sz=64" alt="TripAdvisor" width={24} height={24} className="h-full w-full object-contain" loading="lazy" />
                                </span>
                                <div className="text-[12px] leading-tight">
                                  <p className="text-foreground-950">{t('td.postedOn')}</p>
                                  <span className="font-medium text-secondary-500">Tripadvisor</span>
                                </div>
                              </div>
                            </div>
                          </article>
                        </div>
                      ))}
                    </div>

                    {/* Prev / Next arrows */}
                    <button
                      type="button"
                      aria-label={t('td.prevReview')}
                      onClick={() => scrollReviews('left')}
                      disabled={reviewScroll <= 0}
                      className="absolute left-0 md:left-2 top-1/2 z-20 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-all lg:left-0 lg:size-10 lg:-translate-x-1/2 hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-30 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-lg" />
                    </button>
                    <button
                      type="button"
                      aria-label={t('td.nextReview')}
                      onClick={() => scrollReviews('right')}
                      className="absolute right-0 md:right-2 top-1/2 z-20 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-all lg:right-0 lg:size-10 lg:translate-x-1/2 hover:bg-black/70 disabled:cursor-not-allowed disabled:opacity-30 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-lg" />
                    </button>
                  </div>
                </section>
              </div>

              {/* Right for you */}
              <div id="s-fit" className="scroll-mt-[130px] mt-8">
                <section>
                  <h2 className="mb-3.5 text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">
                    {t('td.rightForYou')}
                  </h2>
                  <div className="overflow-hidden rounded-xl border border-background-200">
                    <div className="px-[22px] pb-[18px] pt-5">
                      <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.13em] text-primary-500">{t('td.honestVersion')}</p>
                      <p className="text-[13.5px] leading-[1.65] text-foreground-950">{tour.right_for_you?.honest_version}</p>
                    </div>
                    <div className="grid grid-cols-1 border-t border-background-200 lg:grid-cols-2">
                      <div className="p-[18px_22px_20px]">
                        <p className="mb-3 flex items-center gap-2 text-[13.5px] font-bold text-foreground-950">
                          <span className="grid size-[22px] place-items-center rounded-md bg-primary-100 text-primary-600">
                            <i className="ri-check-line text-xs" />
                          </span>
                          {t('td.goodIf')}
                        </p>
                        <ul className="grid list-none gap-2.5">
                          {(tour.right_for_you?.goodIf || []).map((item: string, idx: number) => (
                            <li key={idx} className="relative pl-4 text-[12px] leading-[1.6] text-secondary-600">
                              <span className="absolute left-0 top-1.5 size-[5px] rounded-full bg-primary-500" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-[18px_22px_20px] border-t border-background-200 lg:border-l lg:border-t-0">
                        <p className="mb-3 flex items-center gap-2 text-[13.5px] font-bold text-foreground-950">
                          <span className="grid size-[22px] place-items-center rounded-md bg-red-50 text-red-500">
                            <i className="ri-close-line text-xs" />
                          </span>
                          {t('td.notIf')}
                        </p>
                        <ul className="grid list-none gap-2.5">
                          {(tour.right_for_you?.notIf || []).map((item: string, idx: number) => (
                            <li key={idx} className="relative pl-4 text-[12px] leading-[1.6] text-secondary-600">
                              <span className="absolute left-0 top-1.5 size-[5px] rounded-full bg-red-300" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-background-200 bg-primary-50/50 p-[16px_22px]">
                      <p className="max-w-[66ch] text-[12px] leading-[1.6] text-secondary-600">
                        <b className="font-bold text-primary-700">{t('td.nextStepBold')}</b> {t('td.nextStepRest')}
                      </p>
                      <Link
                        to={`/contact?tour=${encodeURIComponent(tour.title)}`}
                        className="rounded-[10px] bg-primary-500 px-6 py-3 text-[13.5px] font-bold whitespace-nowrap text-white hover:bg-primary-600 transition-colors inline-flex items-center justify-center"
                      >
                        {t('td.contactExpert')}
                      </Link>
                    </div>
                  </div>
                </section>
              </div>

              {/* FAQ */}
              <div id="s-faq" className="scroll-mt-[130px] mt-8">
                <section>
                  <h2 className="mb-3.5 text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.beforeContact')}</h2>
                  <div className="overflow-hidden rounded-xl border border-background-200">
                    {faqs.map((item, idx) => (
                      <div key={item.id} className={`border-background-200/70 ${idx > 0 ? 'border-t' : ''}`}>
                        <button
                          onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                          className={`flex w-full items-center justify-between gap-3.5 px-5 py-[15px] text-left text-[13.5px] font-semibold transition-colors hover:text-primary-600 ${
                            faqOpen === idx ? 'text-primary-600' : 'text-foreground-950'
                          }`}
                        >
                          <span>{item.question}</span>
                          {faqOpen === idx ? (
                            <i className="ri-subtract-line text-secondary-400 shrink-0" />
                          ) : (
                            <i className="ri-add-line text-secondary-400 shrink-0" />
                          )}
                        </button>
                        {faqOpen === idx && (
                          <div className="px-5 pb-[17px] text-[13.5px] leading-[1.7] text-secondary-600">
                            {item.answer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </main>

            {/* Sidebar */}
            <aside className="sticky top-[126px] flex flex-col gap-3.5">
              <PricingSidebar
                basePrice={tour.price}
                currency="USD"
                tourId={tour.id}
                tourTitle={tour.title}
                options={[
                  { label: '3-Star', price: tour.price, description: t('td.option3desc'), info: t('td.option3info') },
                  { label: '4-Star', price: Math.round(tour.price * 1.25), description: t('td.option4desc'), info: t('td.option4info') },
                  { label: '5-Star', price: Math.round(tour.price * 1.5), description: t('td.option5desc'), info: t('td.option5info') },
                ]}
              />
            </aside>
          </div>
        </div>

        {/* Related packages */}
        <section className="mx-auto max-w-[1240px] px-4 md:px-6 mt-[34px] pb-8">
          <div className="mb-3.5 flex items-center justify-between gap-3.5">
            <h2 className="text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.related')}</h2>
            <Link to="/tours" className="text-[12px] font-semibold text-primary-600">{t('td.viewAll')}</Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { titleKey: 'td.rel1.title', price: 1500, durationKey: 'td.rel1.duration', spots: 8, image: 'https://cdn.meetup.travel/crm/gallery/38/1782981680331-ab94x4ncgd6.jpg' },
              { titleKey: 'td.rel2.title', price: 920, durationKey: 'td.rel2.duration', spots: 16, image: 'https://cdn.meetup.travel/images/83aed66b-7f89-4e78-ba39-3d74e614b726.webp' },
              { titleKey: 'td.rel3.title', price: 727, durationKey: 'td.rel3.duration', spots: 4, image: 'https://cdn.meetup.travel/crm/gallery/38/1782982008082-uu2i53psncd.jpg' },
              { titleKey: 'td.rel4.title', price: 681, durationKey: 'td.rel4.duration', spots: 4, image: 'https://cdn.meetup.travel/crm/gallery/38/1782981684450-em5w05fhwgn.jpg' },
            ].map((item) => (
              <Link
                key={item.titleKey}
                to="/tours"
                className="group block h-full cursor-pointer"
                aria-label={t(item.titleKey)}
              >
                <article className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-white shadow-[0_4px_14px_-6px_rgba(15,23,42,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-primary-400 hover:shadow-[0_18px_36px_-18px_rgba(16,185,129,0.35)]">
                  <div className="relative w-full overflow-hidden aspect-[300/220]">
                    <img src={item.image} alt={t(item.titleKey)} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-3.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-white px-2 py-1 text-[11px] font-medium text-foreground-950">
                        {t(item.durationKey)}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md border border-secondary-200 bg-white px-2 py-1 text-[11px] font-medium text-foreground-950">
                        {t('td.spots', { count: item.spots })}
                      </span>
                    </div>
                    <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-foreground-950">
                      {t(item.titleKey)}
                    </h3>
                    <div className="mt-auto flex items-end justify-between gap-2 border-t border-foreground-950/5 pt-3">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-secondary-400">
                          {t('td.from')}
                        </span>
                        <span className="text-[15px] font-bold leading-tight text-primary-600">
                          {item.price.toLocaleString()} USD
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary-500 px-3 py-1.5 text-[12px] font-semibold text-primary-600 transition-colors group-hover:bg-primary-500 group-hover:text-white whitespace-nowrap">
                        {t('td.view')}
                        <i className="ri-arrow-right-line text-xs" />
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </section>

        {/* License bar */}
        <div className="mx-auto max-w-[1240px] px-4 md:px-6 mt-[26px] flex flex-wrap items-center gap-3 rounded-xl border border-background-200 bg-white p-[18px_22px]">
          <div className="flex items-center gap-2 text-[12px] text-secondary-500">
            <b className="font-semibold text-foreground-950">RED LION INTERNATIONAL TRAVEL COMPANY LIMITED</b>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-secondary-500">
            <span>{t('td.license')} <b className="font-semibold text-foreground-950">01-2641/2024/CDLQGVN-GP LHQT</b></span>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-secondary-500">
            <span>{t('td.payments')}</span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}